import _ from 'lodash';
import {
  useEffect,
  useMemo
} from 'react';

import {
  TrendFilter_Input,
  useGetAllListedCompaniesLazyQuery
} from 'generated/graphql';

import { CompanyTableRow } from 'modules/components/layer22';



export interface IData {
  companyTableRows: CompanyTableRow.CompanyTableRowProps[];
}

export function useQuery({
  first, offset, freeTextSearch, trends
}: {
  first: number;
  offset: number;
  freeTextSearch: string;
  trends?: TrendFilter_Input[];
}) {
  const [
    fnQuery,
    tuple
  ] = useGetAllListedCompaniesLazyQuery({
    returnPartialData: true
  });



  const debounced = useMemo(() => _.debounce((first: number, offset: number, freeTextSearch: string, trends?: TrendFilter_Input[]) => {
    fnQuery({
      variables: {
        options: {
          paginationInput: {
            limit: first,
            offset: offset
          },
          filter: {
            timeFrame: 'oneYear',
            ticker: freeTextSearch === '' ? undefined : freeTextSearch,
            trends: trends ? trends : undefined
          }

        }
      }
    });
  }, 250), [fnQuery]);



  useEffect(() => {
    debounced(
      first,
      offset,
      freeTextSearch,
      trends
    );
  }, [
    debounced,
    first,
    freeTextSearch,
    offset,
    trends
  ]);


  return useMemo(
    () => {
      const data: IData = {
        companyTableRows: tuple.data?.listedCompanies?.map((node): CompanyTableRow.CompanyTableRowProps => {
          const secondFromLatestPrice = (node.historicalStockPrice && node.historicalStockPrice.length >= 2)
            ? node.historicalStockPrice[node.historicalStockPrice.length - 2]
            : null;

          return {
            id: node.id,
            symbol: node.ticker,
            name: node.companyName,
            price: node.latestStockPrice?.value ?? null,
            change: node.latestStockPrice?.value.minus(secondFromLatestPrice?.value ?? 0) ?? null,
            percentageChange: node.percentChangeSinceYesterday,
            volume: node.latestStockPrice?.formattedVolume ?? '-',
            avgVol: node.formattedAverageVolume,
            marketCap: node.standardGlAccounts?.marketcap?.mostRecentValue?.formattedValue ?? '-',
            peRatio: node.standardGlAccounts?.pricetoearnings?.mostRecentValue?.value ?? null,
            price52Low: node.latestStockPrice?.fiftyTwoWeekLow ?? null,
            price52High: node.latestStockPrice?.fiftyTwoWeekHigh ?? null
          };
        }) ?? []
      };

      return {
        ...tuple, data
      };
    },
    [tuple]
  );
}
